import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Layout from "@/components/Layout";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { Mail, Gift } from "lucide-react";
import { RegulensMark } from "@/components/RegulensMark";

export default function Register() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refParam = searchParams.get("ref") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [verifyToken, setVerifyToken] = useState(null);
  const [referralValid, setReferralValid] = useState(null); // null=unchecked, true/false
  const [referralBonus, setReferralBonus] = useState("");

  useEffect(() => {
    if (!refParam) return;
    api.get(`/referral/validate/${encodeURIComponent(refParam)}`)
      .then(({ data }) => {
        setReferralValid(!!data.valid);
        if (data.valid) setReferralBonus(data.bonus || "");
      })
      .catch(() => setReferralValid(false));
  }, [refParam]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { email, password };
      if (refParam && referralValid) payload.referral_code = refParam;
      const { data } = await api.post("/auth/register", payload);
      if (data.referral_applied) {
        toast.success("Referral applied — 30 days of free Growth waiting for you!");
      } else {
        toast.success("Account created. Verifying...");
      }
      setVerifyToken(data.verification_token_mock);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const completeVerification = async () => {
    setBusy(true);
    try {
      await api.post("/auth/verify-email", { token: verifyToken });
      await login(email, password);
      toast.success("Email verified! Welcome.");
      navigate("/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  if (verifyToken) {
    return (
      <Layout>
        <Toaster theme="dark" position="top-right" offset={80} />
        <section className="max-w-md mx-auto px-6 py-16">
          <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6" data-testid="verify-card">
            <Mail className="w-10 h-10 text-[var(--rg-sage)] mx-auto" />
            <h2 className="font-heading font-bold text-xl text-[var(--rg-text-strong)] text-center mt-3">Verify your email</h2>
            <div className="mt-4 rounded-md border border-[var(--rg-amber)]/40 bg-[var(--rg-amber-soft)] p-3 text-xs text-[var(--rg-amber)]">
              <strong>MOCKED EMAIL:</strong> In production, a verification email is sent. For this MVP, click below to complete verification instantly.
            </div>
            <Button onClick={completeVerification} disabled={busy} className="w-full mt-4 bg-[var(--rg-gold)] text-[var(--rg-on-gold)] hover:bg-[var(--rg-gold-hover)] font-semibold" data-testid="verify-submit">
              {busy ? "Verifying..." : "Verify & Continue"}
            </Button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <Toaster theme="dark" position="top-right" offset={80} />
      <section className="max-w-md mx-auto px-6 py-16">
        <div className="text-center mb-8">
          <div className="flex justify-center">
            <RegulensMark size={44} theme="dark" />
          </div>
          <h1 className="font-heading font-extrabold text-3xl text-[var(--rg-text-strong)] mt-3">Create your account</h1>
          <p className="text-sm text-[var(--rg-text)] mt-1">Free tier: 1 fresh audit / 30 days. No card required.</p>
        </div>
        <div className="space-y-4 rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6" data-testid="register-form">
          {referralValid === true && (
            <div className="rounded-md border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] p-3 flex items-start gap-2" data-testid="referral-banner">
              <Gift className="w-4 h-4 text-[var(--rg-sage)] mt-0.5" />
              <div className="text-xs text-[var(--rg-sage)]">
                <strong>Referral applied!</strong> You&apos;ll receive {referralBonus || "30 days of free Growth access"} when you sign up.
              </div>
            </div>
          )}
          {referralValid === false && (
            <div className="rounded-md border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-3 text-xs text-[var(--rg-amber)]" data-testid="referral-banner-invalid">
              The referral code in your link could not be verified. You can still register without it.
            </div>
          )}
          <GoogleSignInButton testId="register-google-btn" />
          <div className="flex items-center gap-3 my-2">
            <div className="flex-1 h-px bg-[var(--rg-border)]" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--rg-muted)] font-bold">or</span>
            <div className="flex-1 h-px bg-[var(--rg-border)]" />
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]" data-testid="register-email" />
            </div>
            <div>
              <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Password (min 8 chars)</Label>
              <Input type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]" data-testid="register-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-[var(--rg-gold)] text-[var(--rg-on-gold)] hover:bg-[var(--rg-gold-hover)] font-semibold" data-testid="register-submit">
              {busy ? "Creating..." : "Create Account"}
            </Button>
          </form>
        </div>
        <p className="text-center text-xs text-[var(--rg-text)] mt-3" data-testid="register-reassurance">
          <span className="text-[var(--rg-sage)]">✅</span> 1 free audit · No card required · Verified against official sources
        </p>
        <p className="text-center text-sm text-[var(--rg-text)] mt-4">
          Have an account? <Link to="/login" className="text-[var(--rg-sage)] hover:underline">Sign in</Link>
        </p>
      </section>
    </Layout>
  );
}

